// Общий контракт OMA: сервер, раннер и веб-интерфейс используют одни и те же типы.
// Даты — ISO-строки UTC. Идентификаторы агентов, направлений и сервисов — kebab-case.

export const TASK_STATUSES = [
  'inbox',          // Входящие: поставлена, ещё не разобрана
  'triage',         // Разбор: штаб анализирует и раскладывает по направлениям
  'in_progress',    // В работе
  'waiting_agents', // Ожидание: ответов агентов, подзадач, времени или события
  'waiting_human',  // Ждёт тебя: нужно бизнес-решение или уточнение
  'review',         // Проверка: тестировщик и валидатор
  'done',           // Готово
  'cancelled',      // Отменено
] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  inbox: 'Входящие',
  triage: 'Разбор',
  in_progress: 'В работе',
  waiting_agents: 'Ожидание',
  waiting_human: 'Ждёт тебя',
  review: 'Проверка',
  done: 'Готово',
  cancelled: 'Отменено',
}

export type Priority = 'low' | 'normal' | 'high' | 'urgent'
export type TaskKind = 'task' | 'cross' | 'question' | 'incident' | 'review' | 'docs' | 'routine'
export type SafetyMode = 'read_only' | 'pr_only' | 'full'

export const SAFETY_MODE_LABELS: Record<SafetyMode, string> = {
  read_only: 'Только чтение',
  pr_only: 'Ветки и PR',
  full: 'Полный (с одобрением)',
}

export type AgentKind = 'orchestrator' | 'lead' | 'service' | 'reviewer' | 'librarian' | 'worker' | 'external'
export type AgentStatus = 'idle' | 'busy' | 'paused'

export interface Direction {
  id: string
  name: string
  description: string
  goals: string
  lead_agent_id: string | null
  human_owner: string | null
  color: string
  sort: number
}

export interface ServiceDependencyRef {
  service: string
  what: string
  how: string
}

/** Карточка сервиса из каталога (data — полный JSON карточки). */
export interface ServiceCard {
  id: string
  name: string
  direction_id: string | null
  purpose: string
  repo: string | null
  server: string | null
  domains: string[]
  stack: string
  runtime: string
  deploy: string
  databases: string[]
  external_integrations: string[]
  depends_on: ServiceDependencyRef[]
  consumers: ServiceDependencyRef[]
  contracts: string[]
  owners: string[]
  critical_rules: string[]
  key_entities: string[]
  business_processes: string[]
  change_risk_notes: string
  local_path?: string | null
  updated_at?: string
}

export interface Dependency {
  id: number
  from_service: string
  to_service: string
  what: string
  how: string
  evidence: string | null
}

export interface Agent {
  id: string
  name: string
  kind: AgentKind
  direction_id: string | null
  service_ids: string[]
  title: string
  mission: string
  instructions: string
  model: string
  effort: string | null
  max_parallel: number
  status: AgentStatus
  avatar: string
  archived: boolean
  created_at: string
  updated_at: string
  // вычисляемые поля (в списках)
  active_models?: { provider: string; model: string | null }[]
  running_jobs?: number
  queued_jobs?: number
  current_task_ids?: number[]
  jobs_total?: number
  cost_total_usd?: number
}

export interface WaitingOn {
  type: 'agent' | 'human' | 'subtasks' | 'review' | 'timer' | 'external'
  ids: (string | number)[]
  /** Чего ждёт задача — словами для владельца: «подзадача OMA-231 · ответ «Лид CRM»». */
  note?: string
  /** Ближайшее ожидание по времени (wait_until): агент сам вернётся к задаче в этот момент. */
  until?: string
  /** Для external: кроме сообщений человека принимается ответ именно этого агента. ids — задачи-источники. */
  source_agent_id?: string
  /** Для external: старые сообщения не снимают новое ожидание. */
  after_message_id?: number
  /** Только для явного ожидания ответа в связанной задаче Битрикс24; не полномочия владельца. */
  source_bitrix_task?: number
  after_bitrix_message_id?: number
  source_after_at?: string
  source_chat_dialog?: string
  source_chat_root_id?: number
  after_chat_message_id?: number
}

export interface Task {
  id: number
  key: string // OMA-12
  title: string
  description: string
  kind: TaskKind
  status: TaskStatus
  priority: Priority
  direction_ids: string[]
  service_ids: string[]
  parent_id: number | null
  depends_on: number[]
  assignee_agent_id: string | null
  created_by: string // human:<email> | agent:<id> | system
  mode: SafetyMode | null // переопределение режима безопасности для задачи
  acceptance: string
  result: string
  /** Краткий итог для владельца: 1–3 предложения простым языком, без технических подробностей. */
  summary: string
  waiting_on: WaitingOn | null
  risk: 'low' | 'medium' | 'high' | null
  needs_attention: boolean
  nudges: number // сколько раз движок напоминал исполнителю закрыть задачу
  sort: number
  created_at: string
  updated_at: string
  closed_at: string | null
  /** Когда владелец увидел готовую задачу; пусто — готова, но ещё не просмотрена. */
  owner_seen_at: string | null
  /** Черновик сообщения в чат задачи Битрикс24 от лица владельца (коллегам на «ты»); отправляет владелец. */
  chat_message: string
  /** Когда сообщение отправлено в Битрикс24; пусто — ещё нет. */
  chat_posted_at: string | null
  /** Когда исполнитель обещает закончить (ставит сам через update_task). */
  eta: string | null
}

/** Что держит задачу прямо сейчас (в ней или в подзадаче любого уровня). */
export interface NowBlocker {
  task_id: number
  key: string
  title: string
  kind: 'working' | 'queued' | 'owner' | 'agent_answer' | 'timer' | 'review' | 'deps' | 'stalled' | 'external'
  text: string
  agent_id?: string | null
  since?: string | null
  until?: string | null
}

/** «Сейчас» по задаче — первым в карточке: что происходит, чего ждём, когда следующее действие и обещанный срок. */
export interface TaskNow {
  state: 'working' | 'queued' | 'waiting_owner' | 'waiting' | 'review' | 'stalled' | 'done' | 'cancelled'
  headline: string
  blockers: NowBlocker[]
  next_at: string | null
  eta: string | null
  last_activity_at: string
}

/** Карточка задачи на доске — задача + сводка. */
export interface TaskCard extends Task {
  subtasks_total: number
  subtasks_done: number
  running: boolean // сейчас работает хотя бы один агент
  queued: boolean // агент поставлен в очередь, ждёт свободного раннера
  open_escalations: number
  messages_count: number
  last_activity_at: string
  participants: string[] // id агентов, участвовавших в задаче
  /** «Готово»: сообщения в связанной задаче Битрикс24 после закрытия — сколько и последнее */
  bitrix_news?: { count: number; author: string; text: string } | null
}

export type MessageKind =
  | 'note' | 'question' | 'answer' | 'impact' | 'handoff' | 'status'
  | 'escalation' | 'decision' | 'change' | 'review' | 'comment' | 'result' | 'supervise'

export interface Message {
  id: number
  task_id: number | null
  thread_id: number | null
  reply_to: number | null
  from_type: 'agent' | 'human' | 'system'
  from_id: string
  to_type: 'agent' | 'direction' | 'human' | 'broadcast' | 'task'
  to_id: string | null
  kind: MessageKind
  subject: string
  body: string
  data: Record<string, unknown>
  requires_reply: boolean
  answered_by: number | null
  created_at: string
}

export type EscalationType = 'business_decision' | 'clarification' | 'approval' | 'blocker' | 'message' | 'action'

export const ESCALATION_TYPE_LABELS: Record<EscalationType, string> = {
  business_decision: 'Бизнес-решение',
  clarification: 'Уточнение задачи',
  approval: 'Одобрение действия',
  blocker: 'Блокер',
  message: 'Сообщение на отправку',
  action: 'Нужно ваше действие',
}

export interface Escalation {
  id: number
  task_id: number | null
  agent_id: string
  job_id: number | null
  /** Агент просит секрет (токен, пароль): владелец вводит значение в скрытое поле, агент его не видит. */
  secret_name?: string | null
  /** Агент подготовил сообщение от лица владельца: владелец правит текст и отправляет кнопкой (в задачу Битрикс24) или сам. */
  post_text?: string | null
  post_target?: 'bitrix_task' | 'manual' | null
  post_bitrix_task?: number | null
  posted_at?: string | null
  /** попытка отправки оборвалась — сообщение могло уйти; повтор — только после проверки */
  post_attempt_at?: string | null
  type: EscalationType
  question: string
  options: string[]
  recommendation: string
  context: string
  status: 'open' | 'answered' | 'cancelled'
  answer: string | null
  answered_by: string | null
  created_at: string
  answered_at: string | null
  // для списков
  task_key?: string
  task_title?: string
  /** Корневая задача (задача владельца), к которой относится вопрос. */
  root_id?: number
  root_key?: string
  root_title?: string
}

export interface Decision {
  id: number
  title: string
  decision: string
  rationale: string
  scope_type: 'global' | 'direction' | 'service'
  scope_id: string | null
  source: string
  escalation_id: number | null
  created_by: string
  created_at: string
  active: boolean
}

export type KnowledgeKind = 'note' | 'rule' | 'fact' | 'howto' | 'incident' | 'adr' | 'process'

export interface Knowledge {
  id: number
  scope_type: 'global' | 'direction' | 'service' | 'agent'
  scope_id: string | null
  kind: KnowledgeKind
  title: string
  body: string
  source: string
  author: string
  pinned: boolean
  created_at: string
  updated_at: string
}

export type IntentKind = 'code' | 'config' | 'migration' | 'prod-op' | 'deploy' | 'data' | 'contract'

export interface Intent {
  id: number
  agent_id: string
  task_id: number | null
  job_id: number | null
  service_id: string
  area: string
  kind: IntentKind
  description: string
  status: 'active' | 'released' | 'expired'
  created_at: string
  expires_at: string
  released_at: string | null
  outcome: string | null
}

export interface ChangeEvent {
  id: number
  service_id: string | null
  repo: string | null
  source: 'github-commit' | 'github-pr' | 'deploy' | 'agent' | 'human'
  ref: string
  title: string
  author: string
  url: string | null
  happened_at: string
  created_at: string
}

export type JobStatus = 'queued' | 'running' | 'waiting' | 'done' | 'failed' | 'cancelled'
export type JobTrigger = 'task' | 'message' | 'answer' | 'review' | 'wake' | 'nudge'

export interface Job {
  id: number
  agent_id: string
  task_id: number | null
  trigger: JobTrigger
  status: JobStatus
  prompt: string
  session_id: string | null
  runner_id: string | null
  provider?: string | null
  model: string | null
  cost_usd: number
  input_tokens: number
  output_tokens: number
  turns: number
  result: string | null
  error: string | null
  attempt: number
  created_at: string
  started_at: string | null
  finished_at: string | null
}

export type JobEventType = 'text' | 'thinking' | 'tool_use' | 'tool_result' | 'system' | 'error' | 'result'

export interface JobEvent {
  id: number
  job_id: number
  seq: number
  type: JobEventType
  data: Record<string, unknown> // text: {text}; tool_use: {name, input}; tool_result: {tool_use_id, output, is_error}
  created_at: string
}

export interface Runner {
  machine_id?: string
  provider?: 'claude' | 'codex'
  desired_state?: FleetState
  effective_state?: FleetState
  effective_capacity?: number
  transfer_pending?: number
  transfer_blocked_reason?: string | null
  model?: string | null
  strong_model?: string | null
  id: string
  name: string
  labels: string[]
  capacity: number
  version: string
  last_seen: string
  status: 'online' | 'offline'
  running: number
}

export type FleetState = 'active' | 'draining' | 'paused'
export type ModelProvider = 'claude' | 'codex'
export interface Machine {
  id: string
  name: string
  state: FleetState
  capacity: number | null
  reported_capacity: number | null
  running: number
  status: 'online' | 'offline'
  runners: Runner[]
}
export type ModelProfiles = Record<ModelProvider, { standard: string; strong: string }>

export interface Activity {
  id: number
  actor_type: 'agent' | 'human' | 'system' | 'runner'
  actor_id: string
  action: string
  target_type: string | null
  target_id: string | null
  summary: string
  data: Record<string, unknown>
  created_at: string
}

export interface Settings {
  preferred_provider: ModelProvider
  provider_fallback: boolean
  model_profiles: ModelProfiles
  safety_mode: SafetyMode
  max_concurrency: number
  daily_budget_usd: number
  review_required: boolean
  auto_triage: boolean
  blocked_hosts: string[]
  /** Серверы чужих команд: даже в полном режиме команды на них проверяются как «только чтение». */
  readonly_hosts: string[]
  default_model: string
  paused: boolean // глобальная пауза: новые запуски не стартуют
  docs_refresh: 'daily' | 'off' // ежедневная актуализация справочников по коммитам
  /** Время агентов (IANA): по нему OMA называет агентам время, а интерфейс — сроки ожиданий. */
  agent_timezone: string
}

/** Рабочий чат направления в Битрикс24, который читает робот OMA. observe — только разбор (в чат ничего не уходит), work — вживую. */
export type WorkChatMode = 'off' | 'observe' | 'work'
export interface WorkChat {
  dialog_id: string
  title: string
  direction_id: string | null
  /** разборщик чата: решает по каждой ветке — задача, продолжение задачи, быстрый ответ или «не наше» */
  agent_id: string
  /** кому по умолчанию идут задачи из чата */
  assignee_agent_id: string | null
  mode: WorkChatMode
  /** правила этого чата для разборщика и исполнителей: кто есть кто, что брать, чего не трогать */
  notes: string
  /** правила — черновик робота, владелец их ещё не сохранял */
  notes_draft?: boolean
  /** приветствие для включения «работы»: своё у чата (черновик робота или уже отправленное) или общее по направлению */
  intro?: string
  daily_budget_usd: number
  last_message_id: number
  triage_task_id: number | null
  /** бот OMA — участник чата (когда проверено или добавлен); null — не проверено или не участник */
  bot_member_at: string | null
  created_at: string
  updated_at: string
  stats?: { messages_today: number; tasks_today: number; open_tasks: number; pending: number; cost_today_usd: number }
}

/** Чат, найденный по названию или ссылке для подключения. */
export interface ChatCandidate { dialog_id: string; title: string; last_at: string | null; connected: { direction_id: string | null; mode: WorkChatMode } | null }
/** Итог подключения чата: бот добавлен, уже был в чате, чат подключён без бота (добавить вручную) или почему не подключён. */
export interface ConnectResult { dialog_id: string; title: string; status: 'added' | 'already' | 'no_bot' | 'error'; error?: string; moved_from?: string | null }
/** Поиск чатов для подключения: по названию или по ссылке (тогда problem — почему ссылка не подходит). */
export interface ChatSearch { items: ChatCandidate[]; link: boolean; problem?: string }

/** Вид работы в чате и готовность робота делать её самому. */
export interface ChatReportArea {
  name: string
  /** доля этого вида в работе разработчиков направления, % (бизнес-решения в готовность не входят) */
  share: number
  readiness: 'ready' | 'partly' | 'not_ready'
  /** какую часть этой работы агенты сделают сами от начала до конца, %; из неё считается общая готовность */
  pct?: number
  note: string
  needs?: string
}
/** Чат направления в анализе: зачем он, какую долю работы даёт. */
export interface ChatReportChat {
  dialog_id: string
  title: string
  /** роль чата: «здесь ставят задачи по упавшим выкупам», «уведомления робота» */
  role: string
  /** доля работы направления, % */
  share: number
  note: string
}
/** Анализ направления роботом (по всем его чатам): что происходит и насколько команда агентов готова работать сама. */
export interface ChatReport {
  id: number
  /** направление; null — чат без направления (тогда анализ одного чата dialog_id) */
  direction_id: string | null
  dialog_id: string
  agent_id: string
  readiness: number
  level: string
  headline: string
  analysis: string
  areas: ChatReportArea[]
  chats: ChatReportChat[]
  /** что агенты возьмут сами */
  agents_take: string[]
  /** что остаётся людям */
  people_keep: string[]
  /** где теряется время сейчас */
  time_lost: string[]
  /** чего не хватает агентам, чтобы взять больше: доступы, правила, решения, проверка */
  needs: string[]
  basis: string
  created_at: string
}
/** Страница «Чаты»: направление (или чат без направления) с его чатами и анализом. */
export interface WorkChatGroup {
  /** id направления или dialog_id чата без направления */
  key: string
  direction_id: string | null
  direction_name: string | null
  chats: { chat: WorkChat; chat_url: string; verdicts_7d: Record<string, number> }[]
  report: ChatReport | null
  history: { created_at: string; readiness: number }[]
  verdicts_7d: Record<string, number>
  report_pending: boolean
  budget_usd: number
  cost_today_usd: number
  shadow: ShadowScore
}

/** Исход сверки теневого разбора с работой людей. */
export type ShadowOutcome = 'match' | 'agent_better' | 'human_better' | 'human_none' | 'agent_wrong' | 'unclear'

export const SHADOW_OUTCOME_LABELS: Record<ShadowOutcome, string> = {
  match: 'совпало', agent_better: 'агент лучше', human_better: 'люди лучше', human_none: 'люди не ответили', agent_wrong: 'агент ошибся', unclear: 'неясно',
}

/** Теневой режим направления: счёт за 7 дней, расход сегодня и настройки. */
export interface ShadowScore {
  enabled: boolean
  budget_usd: number
  cost_today_usd: number
  total_budget_usd: number
  total_cost_today_usd: number
  /** разобрано агентом за 7 дней */
  analyzed_7d: number
  /** сверено с людьми за 7 дней */
  compared_7d: number
  outcomes_7d: Partial<Record<ShadowOutcome, number>>
  /** медиана минут от сообщения до итога агента / до первого ответа людей по существу */
  agent_minutes_median: number | null
  human_minutes_median: number | null
  recent: ShadowCase[]
}

export interface ShadowCase {
  id: number
  dialog_id: string
  message: string
  task_id: number
  task_key: string
  task_status: TaskStatus | null
  outcome: ShadowOutcome | null
  note: string
  human_minutes: number | null
  agent_minutes: number | null
  created_at: string
  compared_at: string | null
}

export interface Stats {
  tasks_by_status: Record<TaskStatus, number>
  jobs_running: number
  jobs_queued: number
  jobs_today: number
  cost_today_usd: number
  cost_7d_usd: number
  agents_total: number
  agents_busy: number
  open_escalations: number
  /** Ваши задачи, которые агенты закончили, а вы ещё не посмотрели. */
  done_unseen: number
  /** Ближайшее ожидание по времени: в этот момент агент сам вернётся к задаче. */
  next_wake_at: string | null
  active_intents: number
  runners_online: number
}

export interface TaskDetail {
  task: Task
  parent: Task | null
  subtasks: TaskCard[]
  messages: Message[]
  jobs: Job[]
  escalations: Escalation[]
  intents: Intent[]
  activity: Activity[]
  /** Вложения задачи и её родителей (from_task — к какой задаче приложен файл). */
  attachments?: AttachmentInfo[]
  /** Что происходит прямо сейчас во всём дереве задачи. */
  now?: TaskNow
  /** id всех подзадач дерева: их вопросы владельцу — в escalations основной задачи. */
  subtree_ids?: number[]
}

/** Вложение задачи: скриншот или документ владельца. */
export interface AttachmentInfo {
  id: number
  task_id: number | null
  from_task?: number
  name: string
  mime: string
  size: number
  uploaded_by: string
  created_at: string
}

/** Задание для раннера: всё, что нужно, чтобы запустить агента. */
export interface JobSpec {
  job_id: number
  attempt: number // попытка: отчёты раннера по устаревшей попытке не принимаются
  agent: Pick<Agent, 'id' | 'name' | 'kind' | 'title' | 'model' | 'effort' | 'direction_id' | 'service_ids'>
  task_key: string | null
  prompt: string
  fresh_prompt: string // полный бриф — если сессию продолжить не удалось (другая машина)
  system_append: string
  resume_session_id: string | null
  resume_runner_id: string | null
  /** Provider changed: prompt contains a fresh brief and the task's durable OMA history. */
  handoff?: { from_provider: 'claude' | 'codex'; from_job_id: number; from_runner_id: string | null; reason?: 'provider_changed' | 'machine_changed' | 'transfer' | 'session_missing' }
  execution?: { provider: ModelProvider; model: string; effort: string }
  checkpoint?: { id: number }
  mode: SafetyMode
  mcp: { url: string; token: string }
  repos: { repo: string; service_id: string }[]
  blocked_hosts: string[]
  readonly_hosts?: string[]
  /** Только эти инструменты (встроенные и mcp__oma__*); не задано — все. Разбор рабочего чата — без команд и сети. */
  tools?: string[]
  max_turns: number
  max_budget_usd: number
  /** Вложения задачи (и её родителей): раннер кладёт их в рабочую папку по пути path. */
  attachments?: { id: number; name: string; size: number; mime: string; path: string }[]
  /**
   * Команды, которые владелец разрешил вопреки защите (request_approval → «Разрешить один раз»): дословно, по отпечатку.
   * quiet — отказ был из-за секретов: выполнить без вывода, агенту — только код выхода.
   */
  approved?: { id: number; digest: string; quiet: boolean }[]
}

/** Событие живого потока (SSE /api/stream). */
export type StreamEvent =
  | { type: 'task'; data: Task }
  | { type: 'message'; data: Message }
  | { type: 'job'; data: Job }
  | { type: 'job_event'; data: JobEvent }
  | { type: 'escalation'; data: Escalation }
  | { type: 'agent'; data: Agent }
  | { type: 'activity'; data: Activity }
  | { type: 'intent'; data: Intent }
  | { type: 'change'; data: ChangeEvent }
  | { type: 'settings'; data: Settings }
  | { type: 'runner'; data: Runner }

/** Сообщение о лимите подписки Claude, которое SDK иногда отдаёт вместо итога, даже со статусом success. */
export const QUOTA_TEXT = /^(?:You['’]ve hit your (?:session|usage|weekly|daily|monthly) limit|Claude (?:AI )?usage limit reached|You['’]ve reached your usage limit)/i

/** Итог запуска — на самом деле сообщение о лимите (короткий текст по шаблону). */
export const isQuotaText = (s: string | null | undefined): boolean => Boolean(s && s.trim().length < 400 && QUOTA_TEXT.test(s.trim()))

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
/** «resets 7:20pm», «resets Oct 20 at 1am» → момент сброса (ближайший в будущем), иначе через 30 минут. Не дальше 8 часов: потом проба. */
export function quotaResetAt(text: string, nowMs = Date.now()): number {
  const m = /resets?\s+(?:([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),?\s+)?(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i.exec(text)
  if (!m) return nowMs + 30 * 60_000
  let h = Number(m[3]) % 12
  if ((m[5] ?? '').toLowerCase() === 'pm') h += 12
  if (!m[5] && Number(m[3]) >= 12) h = Number(m[3])
  const d = new Date(nowMs)
  const mon = m[1] ? MONTHS.indexOf(m[1].toLowerCase()) : -1
  if (mon >= 0) d.setMonth(mon, Number(m[2]))
  d.setHours(h, Number(m[4] ?? 0), 0, 0)
  if (d.getTime() <= nowMs) {
    if (mon >= 0) d.setFullYear(d.getFullYear() + (d.getTime() <= nowMs - 180 * 86_400_000 ? 1 : 0))
    if (d.getTime() <= nowMs) d.setDate(d.getDate() + 1)
  }
  return Math.min(d.getTime() + 60_000, nowMs + 8 * 3600_000)
}

/** Загрузка машины (раннера) за окно: сутки или неделя. Стоимость — в ценах API (на подписке это оценка, а не счёт). */
export interface RunnerUsageWindow {
  runs: number
  tasks: number
  cost_usd: number
  input_tokens: number
  output_tokens: number
  failed: number
  quota_hits: number
  busy_hours: number // место-часы занятости
  utilization: number // доля занятых мест за окно, 0–1
}

/** Машина с учёткой: что идёт сейчас, загрузка, лимиты, последние задачи (страница «Машины»). */
export interface RunnerUsage extends Runner {
  engine: 'claude' | 'codex'
  role: 'main' | 'reserve' | 'pilot' | 'read_only'
  running_jobs: { job_id: number; agent_id: string; started_at: string; task_id: number | null; task_key: string | null; task_title: string | null }[]
  day: RunnerUsageWindow
  week: RunnerUsageWindow
  hourly: number[] // занятые места в среднем по часам за последние сутки, последний — текущий час
  recent_tasks: { task_id: number; task_key: string; task_title: string; task_status: string; last_at: string; runs: number }[]
  last_quota_at: string | null
  paused_until: string | null
  last_auth_failure_at: string | null
}
